import React from 'react';
import { Card, Typography } from '@material-ui/core';
import '../style/NetWorthChart.css';

class NetWorthChart extends React.Component {
  formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return 'N/A';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  formatDate = (dateString) => {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 365) {
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    }
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  }

  displayedAmount = (snapshot) => {
    const amount = Number(snapshot.amount) || 0;
    if (!this.props.excludeHouseEquity) {
      return amount;
    }
    return amount - (Number(snapshot.house_equity) || 0);
  }

  render() {
    const { snapshots } = this.props;

    if (!snapshots || snapshots.length === 0) {
      return null;
    }

    const sortedSnapshots = [...snapshots].sort((a, b) => {
      return new Date(a.created_at) - new Date(b.created_at);
    });

    if (sortedSnapshots.length < 2) {
      return null;
    }

    const width = 100;
    const height = 60;
    const padding = { top: 12, right: 8, bottom: 22, left: 28 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const amounts = sortedSnapshots.map(s => this.displayedAmount(s));
    const minAmount = Math.min(...amounts);
    const maxAmount = Math.max(...amounts);
    const amountRange = maxAmount - minAmount || 1;

    const points = sortedSnapshots.map((snapshot, index) => {
      const x = padding.left + (index / (sortedSnapshots.length - 1)) * chartWidth;
      const amount = this.displayedAmount(snapshot);
      const normalizedAmount = (amount - minAmount) / amountRange;
      const y = padding.top + chartHeight - (normalizedAmount * chartHeight);
      return { x, y, amount, date: snapshot.created_at };
    });

    const pathData = points.map((point, index) => {
      return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
    }).join(' ');

    const areaPath = `${pathData} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`;

    const maxLabels = Math.min(6, sortedSnapshots.length);
    const labelIndices = [];

    if (sortedSnapshots.length <= maxLabels) {
      for (let i = 0; i < sortedSnapshots.length; i++) {
        labelIndices.push(i);
      }
    } else {
      for (let i = 0; i < maxLabels; i++) {
        const index = Math.floor((i / (maxLabels - 1)) * (sortedSnapshots.length - 1));
        labelIndices.push(index);
      }
    }

    return (
      <Card raised className="NetWorthChart-container" elevation={2}>
        <Typography variant="h6" component="h3" className="NetWorthChart-title">
          Net Worth Over Time
        </Typography>
        <div className="NetWorthChart-wrapper">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="NetWorthChart-svg"
            preserveAspectRatio="none"
          >
            <path
              d={areaPath}
              fill="url(#netWorthGradient)"
              opacity="0.2"
            />

            <defs>
              <linearGradient id="netWorthGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#667eea" />
                <stop offset="100%" stopColor="#764ba2" />
              </linearGradient>
            </defs>

            <path
              d={pathData}
              fill="none"
              stroke="#667eea"
              strokeWidth="0.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {points.map((point, index) => {
              const isLabelPoint = labelIndices.includes(index);
              if (!isLabelPoint) return null;
              return (
                <circle
                  key={index}
                  cx={point.x}
                  cy={point.y}
                  r="0.8"
                  fill="#667eea"
                  opacity="0.6"
                />
              );
            })}

            {labelIndices.map((labelIndex, i) => {
              const point = points[labelIndex];
              return (
                <g key={i}>
                  <text
                    x={point.x}
                    y={height - 5}
                    textAnchor="middle"
                    fontSize="2.8"
                    fill="#888"
                    className="NetWorthChart-axis-label"
                  >
                    {this.formatDate(point.date)}
                  </text>
                </g>
              );
            })}

            <text
              x={padding.left - 5}
              y={padding.top + chartHeight + 4}
              textAnchor="end"
              fontSize="2.8"
              fill="#888"
              className="NetWorthChart-axis-label"
            >
              {this.formatCurrency(minAmount)}
            </text>
            <text
              x={padding.left - 5}
              y={padding.top + 4}
              textAnchor="end"
              fontSize="2.8"
              fill="#888"
              className="NetWorthChart-axis-label"
            >
              {this.formatCurrency(maxAmount)}
            </text>
          </svg>
        </div>
      </Card>
    );
  }
}

export default NetWorthChart;
